import type { AdminReportMediaDto } from '@carcheck/shared';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { formatClipDuration } from '../_lib/formatClipDuration';

/**
 * Single media tile for the report detail drawer (AUT-127 / AUT-272).
 * Mirrors the mockup `.media-tile` shape: 4:3 aspect-ratio with the
 * category label pinned to the bottom-left in a small rounded badge.
 * Video tiles overlay a centered play badge — clicking opens the
 * Lightbox at the tile's index, where the clip actually plays.
 *
 * Photo tiles use `<img>` (lazy + decoding async) so the browser can
 * stream the 1080p source. Video tiles NEVER point an `<img>` at the
 * clip itself — they use the (1h-TTL) thumb the media pipeline renders
 * @1s, and when that thumb isn't there yet they fall back to a muted
 * placeholder surface rather than a broken image icon. A walkaround
 * clip uploaded seconds ago is exactly that case.
 *
 * Videos also carry their `m:ss` length so the operator knows what a
 * click costs before spending it.
 *
 * `onError` bubbles to the parent so Lightbox / MediaTab can invalidate
 * the detail query on a 403 / 404 (architect 🟠 #4 belt-and-braces).
 *
 * AUT-429 — a `document` (PDF) tile is a plain link that opens the signed
 * URL in a new tab. It never renders an `<img>` (a PDF in `<img>` is a
 * broken image whose `onError` re-fetched the detail on every mount) and
 * never calls `onClick` / `onLoadError` — PDFs don't go to the Lightbox.
 */
export interface MediaTileProps {
  media: AdminReportMediaDto;
  /** Visible tile sequence label, e.g. "фото 1". */
  label: string;
  onClick: () => void;
  onLoadError?: () => void;
}

/** Centered play badge overlay for video tiles. */
const PlayBadge = (): ReactElement => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 36,
      height: 36,
      borderRadius: '50%',
      bgcolor: 'rgba(15, 107, 58, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
    }}
  >
    <PlayArrowIcon fontSize="small" />
  </Box>
);

/** Muted surface shown for a video whose thumb job hasn't landed yet. */
const ThumbPending = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: tokens.surface.muted,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: 1,
      }}
    >
      <Typography sx={{ fontSize: 10, color: tokens.text.tertiary }}>
        {t('reports.mediaTab.thumbPending')}
      </Typography>
    </Box>
  );
};

const BADGE_SX = {
  fontSize: 10,
  fontWeight: 600,
  fontFamily: 'monospace',
  color: 'text.secondary',
  bgcolor: 'background.paper',
  px: 0.75,
  py: 0.25,
  borderRadius: 0.75,
} as const;

const TILE_SX = {
  position: 'relative',
  aspectRatio: '4 / 3',
  borderRadius: 1.25,
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  textAlign: 'left',
} as const;

/** PDF tile — opens in a new tab, no preview (AUT-429). */
const DocumentTile = ({
  media,
  label,
}: {
  media: AdminReportMediaDto;
  label: string;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <ButtonBase
      component="a"
      href={media.signedUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('reports.mediaTab.pdfOpen', { label })}
      sx={{
        ...TILE_SX,
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        bgcolor: tokens.surface.muted,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', m: 0.75 }}>
        <Typography sx={BADGE_SX}>{label}</Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'primary.main' }}>
          {t('reports.mediaTab.pdfOpenCue')}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <PictureAsPdfOutlinedIcon sx={{ fontSize: 32, color: tokens.text.tertiary }} />
      </Box>
    </ButtonBase>
  );
};

/** Photo / video tile — preview image + Lightbox on click. */
const VisualTile = ({ media, label, onClick, onLoadError }: MediaTileProps): ReactElement => {
  const isVideo = media.mediaType === 'video';
  // Photos: use the signed source directly. Videos: thumb only — the
  // signed URL is an mp4 and would render as a broken image.
  const previewSrc = isVideo ? media.thumbUrl : media.signedUrl;
  const duration = isVideo ? formatClipDuration(media.durationSec) : null;

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={duration !== null ? `${label} · ${duration}` : label}
      sx={{ ...TILE_SX, alignItems: 'flex-end', bgcolor: 'background.default' }}
    >
      {previewSrc !== null ? (
        <Box
          component="img"
          src={previewSrc}
          alt={label}
          loading="lazy"
          decoding="async"
          onError={onLoadError}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : null}
      {previewSrc === null && isVideo ? <ThumbPending /> : null}
      {isVideo ? <PlayBadge /> : null}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          width: '100%',
          m: 0.75,
        }}
      >
        <Typography sx={BADGE_SX}>{label}</Typography>
        {duration !== null ? <Typography sx={BADGE_SX}>{duration}</Typography> : null}
      </Box>
    </ButtonBase>
  );
};

export const MediaTile = (props: MediaTileProps): ReactElement => {
  switch (props.media.mediaType) {
    case 'document':
      return <DocumentTile media={props.media} label={props.label} />;
    case 'photo':
    case 'video':
      return <VisualTile {...props} />;
    default: {
      const unreachable: never = props.media.mediaType;
      return unreachable;
    }
  }
};
